import React, { useEffect, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../../firebase/AuthProvider";
import {
    GetAllHolidaysAsync,
    DeleteHolidayAsync
} from "../../../services/HolidaysService/HolidaysService";
import { GetHolidaysQueryReq, HolidayResponse } from "../../../interfaces";
import { AxiosError } from "axios";
import { showConfirmationDialog } from "../../modal/ConfimationModal/Confimation";
import AddHolidayModal from "./Modals/AddHolidayModal";
import EditHolidayModal from "./Modals/EditHolidayModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faTrash } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import Pagination from "../../ui/Pagination/Pagination";
import { PaginationDto } from "../../../interfaces/Employee/Employee";
import { PAGINATION } from "../../../Data/SD";
import { MdEdit } from "react-icons/md";
import { AiFillDelete } from "react-icons/ai";


const Holiday = () => {
    const { currentUser } = useAuth();

    const [pagination, setPagination] = useState<PaginationDto>({ pageNumber: PAGINATION.PageNumber, pageSize: PAGINATION.PageSize });

    const [loading, setLoading] = useState<{ getHoliday: boolean }>({
        getHoliday: false,
    });

    const Edit = MdEdit as any;
    const Delete = AiFillDelete as any;

    const [totalCount, setTotalCount] = useState<number>(0);
    const [holidays, setHolidays] = useState<HolidayResponse[]>([]);

    const [sort, setSort] = useState<{
        column: string;
        order: "asc" | "desc";
    }>({
        column: "createddate", // title, startdate, enddate, createddate, updateddate
        order: "desc",
    });

    const [modalOpen, setModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [holidayToEdit, setHolidayToEdit] = useState<HolidayResponse | null>(null);

    useEffect(() => {
        GetAllHolidays();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sort, pagination]);

    const formatDate = (dateStr?: string | null) => {
        if (!dateStr) return "--";
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return "--";
        return d.toLocaleString();
    };

    const GetAllHolidays = async () => {
        const token = await currentUser?.getIdToken();
        if (!token) return;

        const payload: GetHolidaysQueryReq = {
            pageNumber: 1,
            pageSize: 10,
            sortBy: sort.column || null,
            sortDirection: sort.order || null,
            from: null,
            to: null,
        };

        try {
            setLoading((prev) => ({ ...prev, getHoliday: true }));
            const res = await GetAllHolidaysAsync(token, payload);
            if (res.success) {
                setTotalCount(res.data.totalCount);
                setHolidays(res.data.items || []);
            } else {
                toast.error(res.message);
            }

        } catch (error: any) {
            toast.error(
                error instanceof AxiosError
                    ? error.response?.data.message
                    : error.message || "Error To Get Holidays"
            );
        } finally {
            setLoading((prev) => ({ ...prev, getHoliday: false }));
        }
    };

    function handleSort(column: string) {
        setSort((prev) => {
            if (prev.column === column) {
                return { column, order: prev.order === "asc" ? "desc" : "asc" };
            }
            return { column, order: "desc" };
        });
    }

    function getSortIcon(column: string) {
        if (sort.column !== column) return "⇅";
        return sort.order === "asc" ? "↑" : "↓";
    }

    const onEdit = (id: number) => {
        const h = holidays.find(x => x.id === id);
        if (h) {
            setHolidayToEdit(h);
            setEditModalOpen(true);
        }
    };

    const onDelete = async (id: number) => {
        const confirmed = await showConfirmationDialog(
            "Confirm delete",
            "Are you sure you want to delete this holiday?",
            "Delete",
            "Cancel",
            "warning"
        );
        if (!confirmed) return;

        const token = await currentUser?.getIdToken();
        if (!token) return;

        try {
            setLoading((prev) => ({ ...prev, getHoliday: true }));
            const res = await DeleteHolidayAsync(token, id);
            if (res.success) {
                toast.success(res.message || "Holiday deleted successfully");
                GetAllHolidays();
            } else {
                toast.error(res.message || "Failed to delete holiday");
            }
        } catch (err: any) {
            toast.error(err instanceof AxiosError ? err.response?.data.message : err?.message || "Error deleting holiday");
        } finally {
            setLoading((prev) => ({ ...prev, getHoliday: false }));
        }
    };

    const onPageChange = (pageNum: number) => {
        setPagination((prev) => ({
            ...prev,
            pageNumber: pageNum
        }))
    }

    const resetPageSize = (pageNumber: number, pageSize: number) => {
        setPagination({
            pageNumber: pageNumber,
            pageSize: pageSize
        })
    }

    return (
        <div className="bg-white rounded-xl p-4 h-[calc(100vh-160px)]">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Holidays</h2>

                <Button
                    variant="primary"
                    className="btn_sm"
                    onClick={() => setModalOpen(true)}
                >
                    Add New Holiday
                </Button>
            </div>

            {/* Responsive Table Wrapper */}
            <div className="relative overflow-auto rounded-xl border h-[calc(100vh-265px)]">
                <table className="min-w-[1200px] w-full border-collapse ">
                    <thead className="sticky top-0 bg-[#F9F6FF] z-10 border-b" style={{ borderRadius: "20px" }}>
                        <tr className="text-left text-sm font-semibold text-gray-700">
                            <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort("title")}>
                                Title {getSortIcon("title")}
                            </th>

                            <th className="px-4 py-3 w-[450px]">
                                Description
                            </th>

                            <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort("startdate")}>
                                Start Date {getSortIcon("startdate")}
                            </th>

                            <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort("enddate")}>
                                End Date {getSortIcon("enddate")}
                            </th>

                            <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort("createddate")}>
                                Created At {getSortIcon("createddate")}
                            </th>

                            <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort("updateddate")}>
                                Updated At {getSortIcon("updateddate")}
                            </th>

                            <th className="px-4 py-3 text-center">
                                Action
                            </th>
                        </tr>
                    </thead>

                    <tbody className="text-sm">
                        {loading.getHoliday && (
                            <tr>
                                <td colSpan={7} className="px-4 py-6 text-center">
                                    <Spinner animation="border" size="sm" /> Loading...
                                </td>
                            </tr>
                        )}

                        {!loading.getHoliday && holidays.length === 0 && (
                            <tr>
                                <td colSpan={7} className="px-4 py-6 text-center text-gray-500">
                                    No holidays found.
                                </td>
                            </tr>
                        )}

                        {!loading.getHoliday &&
                            holidays.map((h) => (
                                <tr
                                    key={h.id}
                                    className="border-b hover:bg-gray-50 transition"
                                >
                                    <td className="px-4 py-3 font-medium whitespace-nowrap">
                                        {h.title}
                                    </td>

                                    {/* Scrollable Description */}
                                    <td className="px-4 py-3">
                                        <div className="max-h-28 overflow-y-auto pr-2 text-gray-700 leading-relaxed">
                                            {h.description}
                                        </div>
                                    </td>

                                    <td className="px-4 py-3 whitespace-nowrap">
                                        {moment(h.startDate).format("MM/DD/YYYY")}
                                    </td>

                                    <td className="px-4 py-3 whitespace-nowrap">
                                        {moment(h.endDate).format("MM/DD/YYYY")}
                                    </td>

                                    <td className="px-4 py-3 whitespace-nowrap">
                                        {formatDate(h.createdAt)}
                                    </td>

                                    <td className="px-4 py-3 whitespace-nowrap">
                                        {formatDate(h.updatedAt)}
                                    </td>

                                    <td className="px-4 py-3 text-center">
                                        <button
                                            onClick={() => onEdit(h.id)}
                                            className="text-black hover:text-blue-800 mr-3"
                                            title="Edit"
                                        >
                                            <Edit size={20} />
                                        </button>

                                        <button
                                            onClick={() => onDelete(h.id)}
                                            className="text-black hover:text-red-800"
                                            title="Delete"
                                        >
                                            <Delete size={20} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {/* {totalCount > 0 && (
                <Pagination
                    currentPage={pagination.pageNumber}
                    totalPages={Math.ceil(totalCount / pagination.pageSize)}
                    onPageChange={onPageChange}
                    pageSize={pagination.pageSize}
                    resetPageSize={resetPageSize}
                />
            )} */}

            {/* Modals */}
            {modalOpen && (
                <AddHolidayModal
                    isOpen={modalOpen}
                    onClose={() => setModalOpen(false)}
                    onSuccess={GetAllHolidays}
                />
            )}

            {editModalOpen && (
                <EditHolidayModal
                    isOpen={editModalOpen}
                    onClose={() => setEditModalOpen(false)}
                    holiday={holidayToEdit}
                    onSuccess={() => {
                        setEditModalOpen(false);
                        GetAllHolidays();
                    }}
                />
            )}
        </div>
    );
};

export default Holiday;